const inlineTags = {
	code: 'CODE',
	del: 'STRIKETHROUGH',
	s: 'STRIKETHROUGH',
	em: 'ITALIC',
	strong: 'BOLD',
	ins: 'UNDERLINE',
	u: 'UNDERLINE',
	sub: 'SUBSCRIPT',
	sup: 'SUPERSCRIPT',
};

// Browsers canonicalize `element.style.color` reads to `rgb(r, g, b)` regardless of how
// the color was authored, so a hex value emitted by the converter comes back as rgb and the
// round-trip is no longer idempotent (PIKA-542). Normalize every parsed color to lowercase
// hex so `#ff0000` and `rgb(255, 0, 0)` collapse to the same stored value.
//
// Contract:
//   - `rgb(...)` / `rgba(...)`  -> 6-digit lowercase hex. The stored model is opaque 6-digit
//     hex, so an `rgba(...)` alpha channel is intentionally dropped (Jira text/background
//     colors are opaque).
//   - anything else (already-hex, named colors, etc.) -> returned lowercased and otherwise
//     unchanged. In a real browser this branch effectively only receives already-canonicalized
//     values; it stays as a defensive passthrough for authored hex and named colors.
export function normalizeColor(value) {
	const rgbMatch = value.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+\s*)?\)$/i);
	if (rgbMatch) {
		const toHex = component => Math.min(255, Number(component)).toString(16).padStart(2, '0');
		return `#${toHex(rgbMatch[1])}${toHex(rgbMatch[2])}${toHex(rgbMatch[3])}`;
	}
	// Strip whitespace on the fallback path too: the old code did `color.replace(/ /g, '')`
	// unconditionally, so a space-separated color the rgb() regex doesn't match (e.g.
	// `rgb(255 0 0)`) must not leave spaces in the stored `color-…` token (PIKA-542).
	return value.replace(/\s+/g, '').toLowerCase();
}

export default function processInlineTag(tag, node, currentStyle) {
	const styleToCheck = inlineTags[tag];
	let inlineStyle;
	if (styleToCheck) {
		inlineStyle = currentStyle.add(styleToCheck).toOrderedSet();
	} else if (node instanceof HTMLElement) {
		inlineStyle = currentStyle;
		const htmlElement = node;
		inlineStyle = inlineStyle
			.withMutations(style => {
				const color = htmlElement.style.color;
				const backgroundColor = htmlElement.style.backgroundColor;
				const fontSize = htmlElement.style.fontSize;
				const fontFamily = htmlElement.style.fontFamily.replace(/^"|"$/g, '');
				const fontWeight = htmlElement.style.fontWeight;
				const textDecoration = htmlElement.style.textDecoration;
				const fontStyle = htmlElement.style.fontStyle;
				if (color) {
					style.add(`color-${normalizeColor(color)}`);
				}
				if (backgroundColor) {
					style.add(`bgcolor-${normalizeColor(backgroundColor)}`);
				}
				if (fontSize) {
					style.add(`fontsize-${fontSize.replace(/px$/g, '')}`);
				}
				if (fontFamily) {
					style.add(`fontfamily-${fontFamily}`);
				}
				if (fontWeight === 'bold') {
					style.add(inlineTags.strong);
				}
				if (textDecoration === 'underline') {
					style.add(inlineTags.ins);
				}
				if (fontStyle === 'italic') {
					style.add(inlineTags.em);
				}
			})
			.toOrderedSet();
	}
	return inlineStyle;
}
